import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronUp, ChevronDown, Download, AlertTriangle, AlertCircle } from 'lucide-react';
import { agedStockApi } from '../../services/agedStockApi';
import { inventoryApi } from '../../services/inventoryApi';
import type { AgedStockFlaggedItem, LotFlagType } from '../../types/agedStock';

interface Props {
    flagType: LotFlagType;
    canEdit: boolean;
    onClose: () => void;
    onFlagRemoved: () => void;
}

type SortKey =
    | 'CompanyCode' | 'ProductCode' | 'ProductName' | 'LotCode'
    | 'WarehouseName' | 'VendorName'
    | 'CurrentStockQty' | 'CurrentStockValueEUR'
    | 'AgeDays' | 'Reason' | 'FlagCreatedAt' | 'FlagCreatedBy';
type SortDir = 'asc' | 'desc';

function formatDateTime(value: string | null): string {
    if (!value) return '—';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('de-CH');
}

function formatNumber(value: number | null | undefined, fractionDigits = 0): string {
    if (value == null) return '—';
    return new Intl.NumberFormat('de-CH', { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }).format(value);
}

function SortHeader({ label, sortKey, currentKey, currentDir, onSort, align }: {
    label: string;
    sortKey: SortKey;
    currentKey: SortKey;
    currentDir: SortDir;
    onSort: (key: SortKey) => void;
    align?: 'left' | 'right';
}) {
    const isActive = currentKey === sortKey;
    return (
        <th
            className={`sticky top-0 px-3 py-2 text-xs font-medium text-gray-500 uppercase cursor-pointer select-none hover:text-gray-700 bg-white ${
                align === 'right' ? 'text-right' : 'text-left'
            }`}
            onClick={() => onSort(sortKey)}
        >
            <span className="inline-flex items-center gap-0.5">
                {label}
                <span className="inline-flex flex-col -space-y-1">
                    <ChevronUp className={`h-3 w-3 ${isActive && currentDir === 'asc' ? 'text-gray-900' : 'text-gray-300'}`} />
                    <ChevronDown className={`h-3 w-3 ${isActive && currentDir === 'desc' ? 'text-gray-900' : 'text-gray-300'}`} />
                </span>
            </span>
        </th>
    );
}

export function AgedStockFlaggedModal({ flagType, canEdit, onClose, onFlagRemoved }: Props) {
    const [items, setItems] = useState<AgedStockFlaggedItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [sortKey, setSortKey] = useState<SortKey>('FlagCreatedAt');
    const [sortDir, setSortDir] = useState<SortDir>('desc');
    const [removingKey, setRemovingKey] = useState<string | null>(null);

    const reload = () => {
        setLoading(true);
        setError(null);
        agedStockApi.getFlaggedItems(flagType)
            .then(data => setItems(data))
            .catch(err => setError(err instanceof Error ? err.message : 'Failed to load flagged items'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        reload();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flagType]);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [onClose]);

    const handleSort = (key: SortKey) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir(key === 'FlagCreatedAt' || key === 'CurrentStockValueEUR' || key === 'AgeDays' ? 'desc' : 'asc');
        }
    };

    const sorted = useMemo(() => {
        return [...items].sort((a, b) => {
            const aVal = a[sortKey];
            const bVal = b[sortKey];
            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;
            const cmp = typeof aVal === 'number' && typeof bVal === 'number'
                ? aVal - bVal
                : String(aVal).localeCompare(String(bVal));
            return sortDir === 'asc' ? cmp : -cmp;
        });
    }, [items, sortKey, sortDir]);

    const handleUnflag = async (item: AgedStockFlaggedItem) => {
        const key = `${item.ItemID}|${item.LotCode}`;
        setRemovingKey(key);
        try {
            await inventoryApi.removeFlag(item.ItemID, flagType, undefined, item.LotCode);
            onFlagRemoved();
            reload();
        } catch (err) {
            console.error('Failed to remove flag:', err);
            setError(err instanceof Error ? err.message : 'Failed to remove flag');
        } finally {
            setRemovingKey(null);
        }
    };

    const handleExport = async () => {
        try {
            await agedStockApi.exportFlaggedItems(flagType);
        } catch (err) {
            console.error('Export failed:', err);
        }
    };

    const Icon = flagType === 'Expired' ? AlertTriangle : AlertCircle;
    const iconColor = flagType === 'Expired' ? 'text-red-600' : 'text-purple-600';

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={onClose} />

            <div className="relative z-10 flex max-h-[90vh] w-full max-w-7xl flex-col rounded-xl bg-white shadow-2xl">
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                    <div>
                        <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
                            <Icon className={`h-5 w-5 ${iconColor}`} />
                            Flagged: {flagType === 'Expired' ? 'Expired' : 'Quality Issue'}
                        </h2>
                        <p className="mt-0.5 text-sm text-gray-500">{items.length} active flag{items.length === 1 ? '' : 's'}</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleExport}
                            disabled={items.length === 0}
                            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            title="Export to Excel"
                        >
                            <Download className="h-4 w-4" /> Excel
                        </button>
                        <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                            <X className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-16">
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                        </div>
                    ) : error ? (
                        <div className="px-6 py-8 text-center text-sm text-red-600">{error}</div>
                    ) : items.length === 0 ? (
                        <div className="px-6 py-12 text-center text-sm text-gray-500">
                            No items flagged as {flagType}.
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead>
                                <tr>
                                    <SortHeader label="Co."         sortKey="CompanyCode"          currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                                    <SortHeader label="Code"        sortKey="ProductCode"          currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                                    <SortHeader label="Product"     sortKey="ProductName"          currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                                    <SortHeader label="Lot"         sortKey="LotCode"              currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                                    <SortHeader label="Warehouse"   sortKey="WarehouseName"        currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                                    <SortHeader label="Vendor"      sortKey="VendorName"           currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                                    <SortHeader label="Stock Qty"   sortKey="CurrentStockQty"      currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" />
                                    <SortHeader label="Value EUR"   sortKey="CurrentStockValueEUR" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" />
                                    <SortHeader label="Age"         sortKey="AgeDays"              currentKey={sortKey} currentDir={sortDir} onSort={handleSort} align="right" />
                                    <SortHeader label="Reason"      sortKey="Reason"               currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                                    <SortHeader label="Flagged At"  sortKey="FlagCreatedAt"        currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                                    <SortHeader label="Flagged By"  sortKey="FlagCreatedBy"        currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
                                    {canEdit && (
                                        <th className="sticky top-0 bg-white px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Action</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {sorted.map(item => {
                                    const key = `${item.ItemID}|${item.LotCode}`;
                                    return (
                                        <tr key={key} className="hover:bg-gray-50">
                                            <td className="whitespace-nowrap px-3 py-1.5 text-gray-700">{item.CompanyCode}</td>
                                            <td className="whitespace-nowrap px-3 py-1.5 text-gray-600">{item.ProductCode}</td>
                                            <td className="px-3 py-1.5 text-gray-900 max-w-xs truncate" title={item.ProductName ?? ''}>
                                                {item.ProductName ?? '—'}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-1.5">
                                                <code className="rounded bg-gray-100 px-1 py-0.5 text-xs">{item.LotCode}</code>
                                            </td>
                                            <td className="px-3 py-1.5 text-gray-700 max-w-[200px] truncate" title={item.WarehouseName ? `${item.WarehouseName} (${item.WarehouseCode})` : item.WarehouseCode}>
                                                {item.WarehouseName ?? item.WarehouseCode}
                                                {item.WarehouseName && <span className="ml-1 text-gray-400">({item.WarehouseCode})</span>}
                                            </td>
                                            <td className="px-3 py-1.5 text-gray-700 max-w-[180px] truncate" title={item.VendorName ?? ''}>
                                                {item.VendorName ?? '—'}
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-1.5 text-right font-mono">{formatNumber(item.CurrentStockQty, 2)}</td>
                                            <td className="whitespace-nowrap px-3 py-1.5 text-right font-mono">{formatNumber(item.CurrentStockValueEUR)}</td>
                                            <td className="whitespace-nowrap px-3 py-1.5 text-right text-gray-700">
                                                {item.AgeDays != null ? `${item.AgeDays}d` : '—'}
                                            </td>
                                            <td className="px-3 py-1.5 text-gray-600 max-w-[260px]" title={item.Reason ?? ''}>
                                                <span className="line-clamp-2">{item.Reason ?? '—'}</span>
                                            </td>
                                            <td className="whitespace-nowrap px-3 py-1.5 text-gray-500 text-xs">{formatDateTime(item.FlagCreatedAt)}</td>
                                            <td className="whitespace-nowrap px-3 py-1.5 text-gray-500 text-xs">{item.FlagCreatedBy}</td>
                                            {canEdit && (
                                                <td className="whitespace-nowrap px-3 py-1.5 text-right">
                                                    <button
                                                        onClick={() => handleUnflag(item)}
                                                        disabled={removingKey === key}
                                                        className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-red-50 hover:text-red-700 hover:border-red-300 disabled:opacity-50"
                                                    >
                                                        {removingKey === key ? '...' : 'Unflag'}
                                                    </button>
                                                </td>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
